import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, LifeBuoy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type DepartmentRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { refusalText } from '../lib/reach';
import { MONTH_NAMES, formatDate, formatDateTime, formatShortTime, isoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Select,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

/**
 * Support mode (operator, 2026-11-01: "it's not a shift it's only when someone start support mode no agent need
 * it can started from mobile only …"; then, the same day: "it's not every day every employee i will chose days and
 * hours and i can also chose when click on day in shifts"). A manager gives someone support hours on chosen days —
 * here, over a range and its weekdays, or on one day of the roster (the day page); inside them the person switches
 * support mode on in the Viganium Team app. This tab lists a month's support days — on now, used or not — and the
 * support mode used lately, with how many tickets each held its person to.
 */

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  employeeCode: string | null;
}

interface SupportDayRow {
  id: string;
  userId: string;
  user: Person;
  date: string;
  startTime: string;
  endTime: string;
  startAt: string;
  endAt: string;
  current: { id: string; startedAt: string; endsAt: string } | null;
  sessions: Array<{ id: string; startedAt: string; endedAt: string | null; endReason: string | null }>;
}

interface SupportSession {
  id: string;
  user: Person;
  startedAt: string;
  endsAt: string;
  endedAt: string | null;
  endReason: 'ENDED' | 'WINDOW_ENDED' | 'REVOKED' | 'SIGNED_OUT' | null;
  endedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  on: boolean;
  tickets: number;
}

type Translate = ReturnType<typeof useI18n>['t'];

/**
 * "18:00 → 23:00", with "Next day" when it runs past midnight, or "All day" when both ends are the
 * same time. The times keep their order in Arabic too: they are their own left-to-right run.
 */
export function SupportHours({ hours }: { hours: { startTime: string; endTime: string } }) {
  const { t } = useI18n();
  if (hours.startTime === hours.endTime) return <span>{t('shifts.supportAllDay')}</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span dir="ltr" className="whitespace-nowrap font-mono tabular">
        {hours.startTime} → {hours.endTime}
      </span>
      {hours.endTime < hours.startTime && <Badge tone="plasma">{t('shifts.supportNextDay')}</Badge>}
    </span>
  );
}

/** The API's refusals of a support-mode change, in the reader's language where it has words for them. */
export function supportRefusal(error: unknown, t: Translate): string {
  const e = error as { details?: { reason?: string } } | null;
  if (e?.details?.reason === 'NO_TICKET_HANDLING') return t('shifts.supportNoTicketsRefused');
  return refusalText(error, t);
}

/** The roster's day page for someone's day — where its support hours are set or taken away. */
export function supportDayLink(day: { userId: string; date: string; user: Person }, back?: 'support'): string {
  return (
    `/shifts/day?userId=${day.userId}&userName=${encodeURIComponent(day.user.fullName)}&date=${day.date}` +
    (back ? `&back=${back}` : '')
  );
}

export function SupportModeTab() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const manages = can(P.SHIFTS_MANAGE);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [departmentId, setDepartmentId] = useState('');
  const [removing, setRemoving] = useState<SupportDayRow | null>(null);

  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const to = isoDate(new Date(Date.UTC(year, month, 0)));

  const { data: departments } = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<{ items: DepartmentRef[] }>('/departments', { pageSize: 100 }),
    enabled: manages,
  });
  const days = useQuery({
    queryKey: ['support-mode', 'days', from, to, departmentId],
    queryFn: () =>
      api.get<{ items: SupportDayRow[]; serverNow: string }>('/support-mode/days', {
        from,
        to,
        departmentId: departmentId || undefined,
      }),
    enabled: manages,
    // "On until 23:00" stays true while the tab is open.
    refetchInterval: 60_000,
  });
  const sessions = useQuery({
    queryKey: ['support-mode', 'sessions'],
    queryFn: () => api.get<{ items: SupportSession[] }>('/support-mode/sessions', { days: 30 }),
    enabled: manages,
    refetchInterval: 60_000,
  });

  const remove = useMutation({
    mutationFn: (row: SupportDayRow) => api.del<{ ended: boolean }>(`/support-mode/days/${row.id}`),
    onSuccess: () => {
      toast.success(t('shifts.supportRemoved'));
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: ['support-mode'] });
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
    onError: (e) => {
      toast.error(supportRefusal(e, t));
      setRemoving(null);
    },
  });

  const shiftMonth = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  const serverNow = new Date(days.data?.serverNow ?? Date.now());

  /** Where a support day stands: on now, used or not once its hours are over, running and not on, or to come. */
  const status = (row: SupportDayRow) => {
    if (row.current) {
      return (
        <span className="flex flex-col items-start gap-0.5">
          <Badge tone="success" dot>
            {t('shifts.supportOn', { time: formatShortTime(row.current.endsAt, locale) })}
          </Badge>
          <span className="text-caption text-faint">
            {t('shifts.supportSince', { time: formatShortTime(row.current.startedAt, locale) })}
          </span>
        </span>
      );
    }
    const started = new Date(row.startAt) <= serverNow;
    const over = new Date(row.endAt) <= serverNow;
    if (row.sessions.length) {
      const times = row.sessions
        .map((s) => `${formatShortTime(s.startedAt, locale)}–${s.endedAt ? formatShortTime(s.endedAt, locale) : '…'}`)
        .join(', ');
      return (
        <span className="text-body-sm">
          {t('shifts.supportUsed')} <span className="tabular text-muted">{times}</span>
        </span>
      );
    }
    if (over) return <span className="text-faint">{t('shifts.supportNotUsed')}</span>;
    if (started) return <Badge tone="warning">{t('shifts.supportNotOn')}</Badge>;
    return <span className="text-faint">{t('shifts.supportUpcoming')}</span>;
  };

  const howEnded = (s: SupportSession): string => {
    if (s.on || !s.endReason) return '—';
    if (s.endReason === 'REVOKED') return t('shifts.supportEnd.REVOKED', { name: s.endedBy ? localized(s.endedBy) : '—' });
    return t(`shifts.supportEnd.${s.endReason}` as TranslationKey);
  };

  return (
    <div className="space-y-5">
      <Card padded={false}>
        <CardHeader
          title={t('shifts.support')}
          subtitle={t('shifts.supportIntro')}
          className="p-5 pb-0"
          action={
            manages && (
              <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/shifts/support/new')}>
                {t('shifts.supportGive')}
              </Button>
            )
          }
        />
        <div className="flex flex-wrap items-center gap-3 px-5 pb-4">
          <div className="flex items-center gap-1">
            <Button size="sm" icon={<ChevronLeft className="h-4 w-4 flip-rtl" />} onClick={() => shiftMonth(-1)} />
            <span className="min-w-[160px] text-center text-title text-[var(--text-primary)]">
              {MONTH_NAMES[locale][month - 1]} {year}
            </span>
            <Button size="sm" icon={<ChevronRight className="h-4 w-4 flip-rtl" />} onClick={() => shiftMonth(1)} />
          </div>
          <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="!w-auto min-w-[180px]">
            <option value="">
              {can(P.USERS_VIEW_ALL) ? `${t('common.department')} — ${t('common.all')}` : t('reach.allMyDepartments')}
            </option>
            {departments?.items.map((d) => (
              <option key={d.id} value={d.id}>{localized(d)}</option>
            ))}
          </Select>
        </div>
        {days.isLoading ? (
          <LoadingBlock />
        ) : days.error ? (
          <ErrorState error={days.error} onRetry={() => void days.refetch()} />
        ) : !days.data?.items.length ? (
          <EmptyState icon={<LifeBuoy className="h-8 w-8" />} title={t('shifts.supportDaysEmpty')} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.date')}</Th>
                <Th>{t('common.employee')}</Th>
                <Th>{t('shifts.supportHours')}</Th>
                <Th>{t('common.status')}</Th>
                {manages && <Th><span className="sr-only">{t('common.edit')}</span></Th>}
              </tr>
            </thead>
            <tbody>
              {days.data.items.map((row) => (
                <Tr key={row.id}>
                  <Td className={clsx('tabular text-body-sm whitespace-nowrap', row.date === isoDate() && 'font-semibold')}>
                    {formatDate(row.date, locale)}
                  </Td>
                  <Td>
                    {localized(row.user)}
                    {row.user.jobTitle && <span className="block text-caption text-faint">{row.user.jobTitle}</span>}
                  </Td>
                  <Td>
                    <SupportHours hours={row} />
                  </Td>
                  <Td>{status(row)}</Td>
                  {manages && (
                    <Td align="end">
                      <span className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Pencil className="h-4 w-4" />}
                          onClick={() => navigate(supportDayLink(row, 'support'))}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setRemoving(row)}>
                          {t('common.remove')}
                        </Button>
                      </span>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <CardHeader title={t('shifts.supportHistory')} subtitle={t('shifts.supportHistoryHint')} className="p-5 pb-0" />
        {sessions.isLoading ? (
          <LoadingBlock />
        ) : sessions.error ? (
          <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
        ) : !sessions.data?.items.length ? (
          <EmptyState icon={<LifeBuoy className="h-8 w-8" />} title={t('shifts.supportNoHistory')} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.employee')}</Th>
                <Th>{t('shifts.supportStarted')}</Th>
                <Th>{t('shifts.supportEnded')}</Th>
                <Th>{t('shifts.supportHowEnded')}</Th>
                <Th align="end">{t('shifts.supportTickets')}</Th>
              </tr>
            </thead>
            <tbody>
              {sessions.data.items.map((s) => (
                <Tr key={s.id}>
                  <Td>{localized(s.user)}</Td>
                  <Td className="tabular text-body-sm">{formatDateTime(s.startedAt, locale)}</Td>
                  <Td className="tabular text-body-sm">
                    {s.on ? (
                      <Badge tone="success" dot>
                        {t('shifts.supportOn', { time: formatShortTime(s.endsAt, locale) })}
                      </Badge>
                    ) : (
                      formatDateTime(s.endedAt ?? s.endsAt, locale)
                    )}
                  </Td>
                  <Td className="text-body-sm">{howEnded(s)}</Td>
                  <Td align="end" className="tabular">{s.tickets}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {removing && (
        <ConfirmDialog
          title={t('shifts.supportRemoveDayTitle', { name: localized(removing.user), date: formatDate(removing.date, locale) })}
          confirmLabel={t('common.remove')}
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>{t('shifts.supportRemoveDayNote')}</p>
          {removing.current && <p className="font-medium text-[var(--text-primary)]">{t('shifts.supportRemoveEndsNow')}</p>}
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Route: `/shifts/support/new` — Give support mode: to the people chosen, on every chosen weekday of a range (or
 * every day of it), between two hours. Days that have support hours already keep theirs unless told otherwise.
 */
export function GiveSupportModePage() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = isoDate();
  const [form, setForm] = useState({
    userIds: [] as string[],
    fromDate: today,
    toDate: isoDate(new Date(Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0))),
    weekdays: [] as number[],
    startTime: '18:00',
    endTime: '23:00',
    overwrite: false,
  });

  const people = useQuery({
    queryKey: ['support-mode', 'people'],
    queryFn: () => api.get<Person[]>('/support-mode/people'),
  });

  const give = useMutation({
    mutationFn: () =>
      api.post<{ written: number; skipped: number }>('/support-mode/days/bulk', {
        ...form,
        weekdays: form.weekdays.length ? form.weekdays : undefined,
      }),
    onSuccess: (result) => {
      toast.success(t('shifts.supportGiven', { written: result.written, skipped: result.skipped }));
      void queryClient.invalidateQueries({ queryKey: ['support-mode'] });
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      navigate('/shifts?tab=support');
    },
    onError: (e) => toast.error(supportRefusal(e, t)),
  });

  const WEEKDAY_LABELS =
    locale === 'ar'
      ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const valid =
    form.userIds.length > 0 && !!form.fromDate && !!form.toDate && form.toDate >= form.fromDate && !!form.startTime && !!form.endTime;

  return (
    <FormPage
      title={t('shifts.supportGive')}
      subtitle={t('shifts.supportIntro')}
      backTo="/shifts?tab=support"
      backLabel={t('nav.shifts')}
      width="lg"
      submitLabel={t('shifts.supportGive')}
      submitting={give.isPending}
      disabled={!valid}
      onSubmit={() => give.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('nav.employees')} required hint={t('shifts.supportPersonHint')}>
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border hairline p-2">
            {people.isLoading ? (
              <LoadingBlock />
            ) : (
              // One a line: a name and its job title read as one.
              (people.data ?? []).map((u) => (
                <div key={u.id}>
                  <Checkbox
                    checked={form.userIds.includes(u.id)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        userIds: e.target.checked ? [...form.userIds, u.id] : form.userIds.filter((x) => x !== u.id),
                      })
                    }
                    label={u.jobTitle ? `${localized(u)} · ${u.jobTitle}` : localized(u)}
                  />
                </div>
              ))
            )}
          </div>
        </Field>

        <FormGrid>
          <Field label={t('common.from')} required>
            <Input type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} />
          </Field>
          <Field label={t('common.to')} required>
            <Input type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} />
          </Field>
        </FormGrid>

        <Field label={t('shifts.weekdays')} hint={t('shifts.supportWeekdaysHint')}>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_LABELS.map((label, index) => (
              <button
                key={index}
                type="button"
                aria-pressed={form.weekdays.includes(index)}
                onClick={() =>
                  setForm({
                    ...form,
                    weekdays: form.weekdays.includes(index)
                      ? form.weekdays.filter((d) => d !== index)
                      : [...form.weekdays, index],
                  })
                }
                className={clsx(
                  'rounded-pill px-3 py-1 text-caption font-medium ring-1 ring-inset',
                  form.weekdays.includes(index)
                    ? 'bg-primary/15 text-primary-600 dark:text-primary-400 ring-primary/40'
                    : 'text-[var(--text-secondary)] ring-[var(--line)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>

        <FormGrid>
          <Field label={t('shifts.supportFrom')} required>
            <Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
          </Field>
          <Field label={t('shifts.supportTo')} required hint={t('shifts.supportHoursHint')}>
            <Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
          </Field>
        </FormGrid>
        {form.startTime && form.endTime && (
          <p className="flex flex-wrap items-center gap-1.5 text-caption text-muted">
            {t('shifts.supportEachDay')} <SupportHours hours={form} />
          </p>
        )}

        <Checkbox
          checked={form.overwrite}
          onChange={(e) => setForm({ ...form, overwrite: e.target.checked })}
          label={t('shifts.supportOverwrite')}
        />
      </div>
    </FormPage>
  );
}
